import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../config/axios';
import { FileText, Loader, AlertCircle, Maximize2, Minimize2, Download } from 'lucide-react';

/**
 * InlinePDFViewer
 * ---------------
 * يعرض ملف PDF مباشرة داخل صفحة الدرس بدون redirect
 * بيجيب signed URL من /lessons/:id/file-url ويعرضه في iframe
 */
const InlinePDFViewer = ({ lessonId, title }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // جلب الـ signed URL من الـ backend
  const { data, isLoading, error, refetch } = useQuery(
    ['pdf-url', lessonId],
    async () => {
      const response = await api.get(`/lessons/${lessonId}/file-url`);
      return response.data;
    },
    {
      enabled: !!lessonId,
      staleTime: 20 * 60 * 1000,      // 20 دقيقة — الـ signed URL صالحة لفترة أطول
      cacheTime: 25 * 60 * 1000,
      retry: 2,
    }
  );

  const pdfUrl = data?.fileUrl || data?.url;

  // ---- Loading State ----
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center">
              <FileText size={32} className="text-blue-600" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
              <Loader size={14} className="animate-spin text-blue-600" />
            </div>
          </div>
          <p className="text-gray-500 font-bold text-sm">جاري تحضير الملف...</p>
        </div>
      </div>
    );
  }

  // ---- Error State ----
  if (error || !pdfUrl) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-gradient-to-br from-red-50 via-white to-rose-50 p-8">
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h3 className="text-lg font-black text-gray-900">تعذّر تحميل الملف</h3>
          <p className="text-gray-500 text-sm font-medium leading-relaxed">
            {error?.response?.data?.error || 'حدث خطأ أثناء تحضير الملف. تأكد من اشتراكك في الكورس.'}
          </p>
          <button
            onClick={() => refetch()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  // ---- PDF Viewer ----
  return (
    <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'min-h-[600px]'}`}>
      
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <FileText size={16} className="text-blue-600" />
          </div>
          <span className="text-sm font-bold text-gray-700 truncate max-w-[200px] sm:max-w-xs">
            {title || 'ملف PDF'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Download Button */}
          <a
            href={pdfUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-primary-600 bg-gray-50 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-all border border-gray-100 hover:border-primary-100"
          >
            <Download size={14} />
            <span className="hidden sm:inline">تحميل</span>
          </a>

          {/* Fullscreen Toggle */}
          <button
            onClick={() => setIsFullscreen(prev => !prev)}
            className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-primary-600 bg-gray-50 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-all border border-gray-100 hover:border-primary-100"
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            <span className="hidden sm:inline">{isFullscreen ? 'تصغير' : 'تكبير'}</span>
          </button>
        </div>
      </div>

      {/* iFrame (use Google Docs Viewer as proxy for Bunny URLs without .pdf extension) */}
      <div className="flex-1 relative bg-gray-100">
        <iframe
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`}
          title={title || 'PDF Viewer'}
          className="w-full h-full border-0"
          style={{ minHeight: isFullscreen ? 'calc(100vh - 52px)' : '560px' }}
          loading="lazy"
        />
      </div>
    </div>
  );
};

export default InlinePDFViewer;
